import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Chip } from '@kerfjs/ui/chip';
import { fillColor } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetText } from '@kerfjs/ui/list-inset-text';
import { ListItem } from '@kerfjs/ui/list-item';
import { Pane } from '@kerfjs/ui/pane';
import { PopupMenu } from '@kerfjs/ui/popup-menu';
import { Row } from '@kerfjs/ui/row';
import { SearchField } from '@kerfjs/ui/search-field';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { Text } from '@kerfjs/ui/text';
import { Toolbar } from '@kerfjs/ui/toolbar';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import { ToolbarText } from '@kerfjs/ui/toolbar-text';
import { Workbench } from '@kerfjs/ui/workbench';
import type { SafeHtml } from 'kerfjs';

import type { SessionUser } from '../../api/schemas.js';
import { isAwaitingSense, type NewsItem } from '../../db/schemas.js';
import type { AppState } from '../app/stores.js';
import {
  appStore,
  FEED_PAGE,
  railCollapsed,
  STARTER_TOPICS,
  TOPIC_SORT_LABELS,
  TOPIC_SORTS,
  visiblePendingTopics,
} from '../app/stores.js';
import { schemeDark, SEARCH_FIELD_ID, searchExpanded, searchRevision } from '../app/view-state.js';
import {
  addTopicDialogJsx,
  capacityDialogJsx,
  checkoutDialogJsx,
  confirmDialogJsx,
  exportDialogJsx,
  renameDialogJsx,
  sensePickerDialogJsx,
  suggestDialogJsx,
} from '../components/dialogs/dialogs.js';
import { filterBarJsx } from '../components/filter-bar.js';
import { icon } from '../components/icons.js';
import { discoverDialogJsx } from '../features/discovery/discover-view.js';
import { feedJsx } from '../features/feed/feed.js';
import { itemMatchesQuery } from '../features/feed/search.js';
import { currentFailure } from '../features/notifications/failure.js';
import {
  isIosNeedingInstall,
  livePermissionState,
  livePlatformProbe,
  pushNudgeVariant,
} from '../features/notifications/re-engagement.js';
import { onboardingJsx } from '../features/onboarding/onboarding-view.js';
import { compactTopicPulseJsx, pulseDialogJsx } from '../features/pulse/pulse-view.js';
import { sessionGateState } from '../features/session/session.js';
import { settingsDialogJsx } from '../features/settings/settings.js';
import { canUpgrade, hasReEnableRoom, planUsageParts } from '../features/topics/capacity.js';
import { isHeading, sortTopics, topicRows } from '../features/topics/topic-sort.js';
import { pendingTopicRowJsx, type TopicMenuSpec, topicRowJsx } from '../features/topics/topics-view.js';

/**
 * What fills the feed panel when there is nothing to show (NEWS-433).
 *
 * The feed used to render *nothing at all* here — a bare dark panel beside a
 * sidebar that carried the only message. Someone who skipped setup landed on a
 * screen that looked broken and gave them no way back in.
 *
 * Four shapes, and the split matters:
 *  - **No topics** — the welcome hero: what the app does, the setup guide (which
 *    was otherwise reachable only through Settings), and a row of popular topics
 *    as one-click adds. This is the "I skipped setup, now what?" case the ticket
 *    is about, so it does the most.
 *  - **Topics but no stories** — reassurance that a check will fill the panel,
 *    the button to run one now, and the setup guide still within reach.
 *  - **A sub-filter that matched nothing** (search / saved / review) — the plain
 *    line it always had. A hero here would shout at a routine empty result and
 *    bury the one fact that matters (nothing matched *this* filter).
 *
 * Every action here is already wired at the root: `rerun-onboarding`,
 * `open-discover`, and `add-starter-topic` (a plain `addTopic`).
 */
export function feedEmptyStateJsx(s: AppState, reviewMode: boolean, searching: boolean): SafeHtml {
  if (reviewMode)
    return <EmptyState icon={icon('flag')} title="No Flagged Stories" detail="No flagged stories for these topics." />;
  if (searching) return <EmptyState icon={icon('search')} title="No Matches" detail="No stories match your search." />;
  if (s.savedFilter) {
    return (
      <EmptyState
        icon={icon('bookmark')}
        title="No Saved Stories"
        detail="No saved stories yet. Use the bookmark button on a story to keep it here."
      />
    );
  }

  const setupGuide = (
    <Button emphasis="secondary" action="rerun-onboarding" icon={icon('guidance')}>
      Show the Setup Guide
    </Button>
  );

  if (s.topics.length > 0) {
    // A check in flight while the panel is empty is the just-added-a-topic case
    // (NM-92): say we are looking now, rather than leaving "No stories yet" to
    // read as though nothing is happening. `busy` shows the spinner.
    const checking = s.checking.length > 0;
    return (
      <EmptyState
        busy={checking}
        icon={checking ? undefined : icon('bell')}
        title={checking ? 'Checking for New Stories' : 'No Stories Yet'}
        detail={
          checking
            ? 'Newsmonger is looking now — new stories will appear here as they are found. This can take a little while against a live provider.'
            : 'Your topics are set. Scheduled checks land new stories here — only what is genuinely new, never a repeat.'
        }
        action={setupGuide}
      />
    );
  }

  return (
    <List gap="l">
      <EmptyState
        icon={icon('search')}
        title="Nothing to Watch Yet"
        detail="Name a topic and Newsmonger keeps up with it for you — asking an AI with live web search, on your schedule, and showing only what has genuinely changed. Add one now, or start from the setup guide."
        action={
          // A real control, not a pointer at "the sidebar" (NM-270): it opens
          // the Add Topic dialog (NM-ZJT0H7), wherever the rail is.
          <Row gap="xs" wrap hAlign="center">
            <Button tone="brand" action="open-add-topic" icon={icon('plus')}>
              Add a Topic
            </Button>
            {setupGuide}
            <Button emphasis="secondary" action="open-discover" icon={icon('grid')}>
              Discover Topics
            </Button>
          </Row>
        }
      />
      <List gap="xs" hAlign="center">
        <Text variant="h3" size="compact" tone="quiet" align="center">
          Popular Topics
        </Text>
        <Row gap="xs" wrap hAlign="center">
          {STARTER_TOPICS.map((name) => (
            <Chip action="add-starter-topic" itemId={name} actionLabel={`Watch ${name}`}>
              {name}
            </Chip>
          ))}
        </Row>
      </List>
    </List>
  );
}

/**
 * The top-level sign-in gate (NM-20, phase 3).
 *
 * The app is gated behind sign-in on the client. `rootJsx` is what the single
 * root `mount()` renders, and it switches on `sessionGateState(session)`:
 * a loading placeholder while the boot fetch is in flight, the sign-in gate when
 * signed out, a blocked screen for a suspended/disabled account, the
 * registration flow for a verified identity with no account, and the app itself
 * (`appJsx`) only once signed in, active and registered. `/api/state` is never
 * loaded and the app never mounts while gated — the poll starts in
 * `enterActiveApp`.
 *
 * A single always-present `<div class="gate-root">` wrapper is *not* used: this
 * is the root the mount owns, and each branch returns exactly one element, which
 * the morph swaps wholesale on a transition — there are no conditional siblings
 * here to keep structurally stable (the app's own live regions live inside
 * `appJsx`).
 */
export function rootJsx(): SafeHtml {
  const s = appStore.state.value;
  const gate = sessionGateState(s.session);
  if (gate.status === 'loading') return gateLoadingJsx();
  if (gate.status === 'signed-out') return signInGateJsx(s.signInError, s.session?.appleSignIn ?? false);
  if (gate.status === 'blocked') return accountBlockedJsx(gate.user);
  if (gate.status === 'unregistered') return registrationJsx(gate.email, s.signInError);
  return appJsx();
}

/** Which gate screen is on show — the `data-gate` hook tests address it by. */
type GateKind = 'loading' | 'signin' | 'blocked' | 'registration';

/**
 * The frame every gate screen shares (NM-6M3FTC), composed from kerf UI.
 *
 * `#app` is a viewport-locked `.kui-app-root`, so the gate is a filling,
 * scrolling `List` and scrolls itself on a short window (a landscape phone).
 * Inside it, one `<main>` centres itself with auto margins rather than flex
 * centring: auto margins only take *positive* free space, so a gate taller than
 * the window starts at its top and scrolls, where `justify-content: center`
 * would push its top out of reach. `data-gate` names the screen for tests.
 *
 * The wordmark is the official asset, not styled text (NEWS-175, NM-197) — the
 * same two-image light/dark pattern as the app bar's.
 */
function gateShellJsx(kind: GateKind, body: SafeHtml): SafeHtml {
  return (
    <List fill scrollable inset="document" rootAttributes={{ 'data-gate': kind }}>
      <main class="gate-page">
        <List gap="l">
          <span class="wordmark" role="img" aria-label="Newsmonger" data-gate-mark="">
            <img class="mark-light" src="/static/wordmark-light.svg" alt="" width="480" height="100" />
            <img class="mark-dark" src="/static/wordmark-dark.svg" alt="" width="480" height="100" />
          </span>
          {body}
        </List>
      </main>
    </List>
  );
}

export function gateLoadingJsx(): SafeHtml {
  return gateShellJsx('loading', <EmptyState busy title="Loading…" />);
}

/** The heading and lead copy each actionable gate opens with. */
function gateHeadingJsx(title: string, lead: readonly (SafeHtml | string)[]): SafeHtml {
  return (
    <List gap="xs">
      <Text variant="h1" size="large" align="center" flush>
        {title}
      </Text>
      {/* Default tone, not `quiet`: kerf's quiet text (#6e6e73) on the reader's
          porcelain paper (#f4f1ea) measures 4.49:1 — just under AA. */}
      <Text align="center" flush>
        {lead}
      </Text>
    </List>
  );
}

/**
 * The gate's always-present error slot. It stays in the tree with nothing in it
 * so the error appearing can't restructure the buttons above it (docs/3-ui.md);
 * the error itself is a danger `StateBanner`, which is its own `role="alert"`.
 */
function gateErrorJsx(title: string, signInError: string | null): SafeHtml {
  return (
    <div class="gate-slot" data-gate-error="">
      {signInError !== null ? (
        <StateBanner tone="danger" urgency="alert" copyLayout="stacked" title={title} detail={signInError} />
      ) : null}
    </div>
  );
}

/**
 * The official 4-colour Google "G" mark (NM-266), for the Sign In with Google
 * button. It is a brand mark, so it stays its own SVG rather than a Lucide icon,
 * passed as the kerf UI `Button`'s leading `icon` (NM-RR1Q7G).
 */
function googleMark(): SafeHtml {
  return (
    <span class="google-mark">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
      </svg>
    </span>
  );
}

export function signInGateJsx(signInError: string | null, appleEnabled: boolean): SafeHtml {
  return gateShellJsx(
    'signin',
    <List gap="l">
      {gateHeadingJsx('Sign In to Newsmonger', [
        'Newsmonger keeps up with the topics you choose — asking an AI with live web search, on your schedule. Sign in to get started.',
      ])}
      <List gap="xs">
        <Button emphasis="secondary" action="sign-in" icon={googleMark()}>
          Sign In with Google
        </Button>
        {/* Sign in with Apple (NM-252), shown only when the deploy has Apple
            configured (`appleSignIn` on the session response) — otherwise its
            button would open a 404. Always-present container so it appearing
            can't restructure its siblings (docs/3-ui.md). Apple's guidance is a
            solid dark button; the neutral loud fill is that, inverting to the
            light variant Apple also allows on a dark scheme. */}
        <div class="gate-slot" data-gate-apple="">
          {appleEnabled ? (
            <Button action="sign-in-apple" icon={icon('apple')}>
              Sign In with Apple
            </Button>
          ) : null}
        </div>
      </List>
      {gateErrorJsx('Couldn’t Sign In', signInError)}
    </List>,
  );
}

export function accountBlockedJsx(user: SessionUser): SafeHtml {
  return gateShellJsx(
    'blocked',
    <List gap="l">
      {gateHeadingJsx('Your Account Isn’t Active', [
        'The account ',
        <strong>{user.email}</strong>,
        ` is ${user.status === 'suspended' ? 'suspended' : 'disabled'}, so Newsmonger can’t be used right now. If you think this is a mistake, get in touch.`,
      ])}
      <Button emphasis="secondary" action="sign-out">
        Sign Out
      </Button>
    </List>,
  );
}

export function registrationJsx(email: string | null, signInError: string | null): SafeHtml {
  return gateShellJsx(
    'registration',
    <List gap="l">
      {gateHeadingJsx(
        'Welcome to Newsmonger',
        [
          'You’re signed in',
          email !== null ? [' as ', <strong>{email}</strong>] : '',
          '. Create your account to set up your topics and start watching the news that matters to you.',
        ].flat(),
      )}
      <List gap="xs">
        <Button tone="brand" action="register">
          Create My Account
        </Button>
        <Button emphasis="plain" action="sign-out">
          Use a Different Account
        </Button>
      </List>
      {gateErrorJsx('Couldn’t Create Your Account', signInError)}
    </List>,
  );
}

export function appJsx(): SafeHtml {
  const s = appStore.state.value;
  const topicNames = new Map(s.topics.map((t) => [t.id, t.name]));
  const solo = new Set(s.soloTopicIds);
  const selected = new Set(s.selectedTopicIds);
  // Topics with a check in flight (NM-92), for the row's "Checking sources…"
  // status. From `/api/state`, so it appears on the next poll after a topic is
  // added or manually re-checked and clears when the check finishes.
  const checking = new Set(s.checking);
  // The feed page is filtered + sorted + paginated by the server for the active
  // view (NEWS-76). Review mode shows ONLY the flagged stories for its topics.
  const reviewMode = s.reviewTopicIds.length > 0;
  const searching = s.searchQuery.trim() !== '';
  const feedVariant: 'normal' | 'review' = reviewMode ? 'review' : 'normal';
  // In the normal view the server excludes off-topic stories, so merge in any
  // flagged *this session* — collapsed, and only if they match the active
  // Solo/Saved/Search — so a misclick stays undoable until reload (NEWS-61).
  let feedItems = s.feedItems;
  if (!reviewMode && s.recentlyFlaggedItems.length > 0) {
    const seen = new Set(s.feedItems.map((i) => i.id));
    const overlay = s.recentlyFlaggedItems.filter(
      (it) =>
        !seen.has(it.id) &&
        (solo.size === 0 || solo.has(it.topicId)) &&
        (!s.savedFilter || it.saved) &&
        itemMatchesQuery(it, topicNames.get(it.topicId) ?? '', s.searchQuery),
    );
    if (overlay.length > 0) {
      feedItems = [...s.feedItems, ...overlay].sort((a, b) => b.foundAt.localeCompare(a.foundAt));
    }
  }
  // "Show more" reflects what the *server* still holds for this view (the
  // session overlay is separate and always shown).
  const moreCount = Math.max(0, s.feedTotal - s.feedItems.length);
  const savedCount = s.feedTotal; // meaningful only while the Saved filter is on
  // Only warn about a topic whose *latest* run failed — not a stale failure from
  // one that has since recovered (NEWS-41).
  const lastFailure = currentFailure(s.runs);
  // Which push nudge to show, if any (NM-272). Browser facts are read here, at
  // render, rather than cached: permission changes under us when the reader
  // answers the prompt, and `notifyPermissionDenied` is the store's record of
  // the attempt that produced it.
  const pushNudge = pushNudgeVariant({
    pushPublicKey: s.pushPublicKey,
    notifyOnNewItems: s.settings.notifyOnNewItems,
    feedLoaded: s.feedLoaded,
    storyCount: s.feedItems.length,
    record: s.pushNudge,
    now: Date.now(),
    permissionDenied: s.notifyPermissionDenied,
    permission: livePermissionState(),
    iosNeedingInstall: isIosNeedingInstall(livePlatformProbe()),
  });

  // The view title the work-area toolbar carries (kerf UI shell, NM-D5TYNV): what
  // the feed is showing right now, so the mode is named where the reader looks.
  const viewTitle = reviewMode
    ? 'Flagged Stories'
    : s.savedFilter
      ? 'Saved Stories'
      : solo.size === 1
        ? (topicNames.get([...solo][0] ?? '') ?? 'Your Feed')
        : 'Your Feed';

  return (
    // The shell is kerf UI's Desktop Application Shell recipe: a filling `List`
    // stacks the app bar above a `Workbench` whose left rail is the Watching
    // manager and whose work area is the feed. Only the real panes scroll
    // (`#app` is a viewport-locked `.kui-app-root`, see `Layout`).
    <List fill rootAttributes={{ 'data-shell': 'reader' }}>
      {appBarJsx(s.searchQuery, railCollapsed.value)}
      <List flex>
        <Workbench
          id={WORKBENCH_ID}
          label="Reader"
          leftRail={{
            label: 'Topics',
            size: RAIL_WIDTH,
            collapsed: railCollapsed.value,
            // No Workbench toolbar: the rail's heading is a kerf UI `ListHeader`
            // (NM-WXNQ4F) and its show/hide control lives in the app bar
            // (NM-614VD8, `railToggleJsx`). Without a toolbar the Workbench
            // renders the content bare, so the content is its own `Pane` — the
            // header pinned above the scrolling list, Upgrade pinned below it.
            content: (
              <Pane
                element="div"
                safeAreaEdges={[]}
                rootAttributes={{ 'data-rail-pane': '' }}
                header={railHeaderJsx(s.topicSort, s.topics.length > 1)}
                footer={
                  // Upgrade (NM-226) in the rail's footer, full width and filled
                  // (NM-MSWSXC): the one money action, pinned below the list
                  // rather than squeezed in beside the usage counts. Only with
                  // mock billing on and a pricier tier to move to.
                  s.plan !== null && canUpgrade(s.billing, s.plan.id) ? (
                    <List controlInsets="rbl">
                      <Button tone="brand" action="open-checkout">
                        Upgrade Plan
                      </Button>
                    </List>
                  ) : undefined
                }
              >
                {railJsx(s, selected, solo, checking)}
              </Pane>
            ),
          }}
          mainToolbar={{
            label: 'Feed',
            title: <ToolbarText text={viewTitle} size="xlarge" headingLevel={2} />,
            responsive: 'wrap',
            trailing: [
              // Adding and discovering topics live with the feed (NM-ZJT0H7):
              // "Add Topic…" opens the add dialog (the ellipsis says so), and
              // Discover is icon-only, named for assistive tech and the tooltip.
              <ToolbarControlGroup single content="mixed" label="Add Topic">
                <button type="button" data-action="open-add-topic">
                  {icon('plus')} Add Topic…
                </button>
              </ToolbarControlGroup>,
              <ToolbarControlGroup single label="Discover Topics">
                <button type="button" data-action="open-discover" aria-label="Discover Topics" title="Discover Topics">
                  {icon('grid')}
                </button>
              </ToolbarControlGroup>,
              <ToolbarControlGroup label="Feed Controls" buttonAppearance="push">
                <button
                  type="button"
                  data-action="toggle-saved-filter"
                  aria-pressed={s.savedFilter ? 'true' : 'false'}
                  aria-label="Saved Stories"
                >
                  {icon('bookmark')}
                </button>
              </ToolbarControlGroup>,
            ],
          }}
          mainHeader={[
            // Section navigation (NM-232, FR-22.10) above the feed only.
            <div id="filter-slot">
              {filterBarJsx(s.categoryFilter, s.topics, s.categoryPulse, s.soloTopicIds.length > 0)}
            </div>,
            bannersJsx(s, {
              solo,
              reviewMode,
              savedCount,
              topicNames,
              lastFailure,
              pushNudge,
            }),
          ]}
          mainPane={{ contentElement: 'section', contentLabel: 'Stories', headerPresentation: 'overlay' }}
          main={feedPaneJsx(s, feedItems, topicNames, feedVariant, solo, reviewMode, searching, moreCount)}
        />
      </List>
      {dialogSlotsJsx(s)}
      {toastJsx(s)}
      {/* Spoken check progress (NM-92): a topic starting a check says so, one
          finishing reports what it found. Visually hidden — the row's "Checking
          sources…" status is the visible channel; this is the same event for a
          screen-reader user. Always present, like every live region, so the
          announcement is not lost. */}
      <div id="check-announce" class="visually-hidden" role="status" aria-live="polite">
        {s.checkAnnouncement}
      </div>
    </List>
  );
}

/**
 * The brand pine per scheme — the loud brand fill `ui-theme.css` sets
 * (`--wa-color-brand-fill-loud`). Spelled out because a scoped tint needs a
 * concrete color: kerf UI's own `--kui-color-brand-*` ramp is its default blue,
 * and the app overrides the `--wa-` brand inputs kerf's foundation reads, not
 * that one.
 */
const BRAND_PINE = { light: '#17604f', dark: '#4da88e' } as const;

/** The Workbench's id; its left rail's region id derives from it (`topics-left-rail`). */
export const WORKBENCH_ID = 'topics';
/** The Watching rail's width, px. */
const RAIL_WIDTH = 320;

/**
 * The app bar (NM-D5TYNV): the wordmark, the story search, and Settings.
 *
 * The search is kerf UI's collapsible `SearchField` (free text only — no
 * tokens), wired by `wireSearchFields` in `wireEvents`: it collapses to an
 * icon when empty and unfocused and stays open while it holds a query. The query
 * is store state; the editor text is DOM-owned between renders.
 */
function appBarJsx(searchQuery: string, railHidden: boolean): SafeHtml {
  const searchOpen = searchExpanded.value || searchQuery !== '';
  return (
    // The app bar takes the brand green (NM-DV3RKT) through a kerf UI scoped
    // `tint` — Toolbar has no surface of its own, so a header-only `Pane`
    // carries it. The tint re-derives the surface, text and control fills
    // from the pine with AA clamps; it must declare which scheme the color
    // belongs to, which is the resolved app theme (`schemeDark`).
    // `flex="none"` keeps it at its content height: as a shrinkable item a Pane
    // collapses to nothing (`min-height: 0`) beside the filling Workbench.
    <List flex="none">
      <Pane
        element="div"
        safeAreaEdges={[]}
        tint={{
          color: fillColor(schemeDark.value ? BRAND_PINE.dark : BRAND_PINE.light),
          scheme: schemeDark.value ? 'dark' : 'light',
        }}
        rootAttributes={{ 'data-app-bar': '' }}
        header={appBarToolbarJsx(searchQuery, railHidden, searchOpen)}
      />
    </List>
  );
}

function appBarToolbarJsx(searchQuery: string, railHidden: boolean, searchOpen: boolean): SafeHtml {
  return (
    <Toolbar
      label="Newsmonger"
      // An open search gets its own full row below the wordmark on a compact
      // bar, where it cannot fit beside the identity (NM-WTGW3P).
      responsive="trailing-priority"
      responsiveAt="compact"
      safeAreaEdges={['block-start', 'inline-start', 'inline-end']}
      dividerSides="b"
      leading={[
        railToggleJsx(railHidden),
        // The wordmark is the brand asset, not styled text (NEWS-175). Both marks
        // ship and CSS shows the one that suits the resolved scheme (NEWS-377);
        // the accessible name is on the <h1>, so it survives whichever is hidden.
        <h1 class="wordmark" aria-label="Newsmonger">
          <img class="mark-light" src="/static/wordmark-light.svg" alt="" width="480" height="100" />
          <img class="mark-dark" src="/static/wordmark-dark.svg" alt="" width="480" height="100" />
        </h1>,
      ]}
      trailing={[
        // Open, the search grows from a usable basis into the room beside the
        // Settings group (kerf UI "Room for an expanded search").
        <ToolbarControlGroup
          single
          content="search"
          expanded={searchOpen}
          sizing={searchOpen ? 'grow' : 'content'}
          growBasis="20rem"
        >
          <SearchField
            id={SEARCH_FIELD_ID}
            label="Search Stories"
            placeholder="Search stories"
            presentation="toolbar-group"
            collapsible
            expanded={searchOpen}
            expandLabel="Search Stories"
            query={searchQuery}
            revision={searchRevision.value}
            clearAction="clear-search"
            clearLabel="Clear Search"
          />
        </ToolbarControlGroup>,
        // Settings yields while the search is open on a compact bar, where the
        // search has the row to itself; closing the search brings it back.
        // The visibility policy shares Toolbar's compact query and boundaries.
        <ToolbarControlGroup single label="App" visibility="yield-to-priority-row">
          <button type="button" data-action="open-settings" aria-label="Settings">
            {icon('settings')}
          </button>
        </ToolbarControlGroup>,
      ]}
    />
  );
}

/**
 * The Topics rail's show/hide toggle (NM-614VD8). The Workbench would render it
 * in the rail's own toolbar while open and move it into the feed's toolbar while
 * collapsed; it lives in the app bar instead, at the leading edge before the
 * wordmark, so it never moves. Outside the region it controls, as kerf UI's
 * Workbench accessibility contract asks, and it names that region with
 * `aria-controls` (`<workbench id>-left-rail`). The labels are the Workbench's
 * own defaults ("Show Topics" / "Hide Topics").
 */
function railToggleJsx(railHidden: boolean): SafeHtml {
  return (
    <ToolbarControlGroup single label="Topics">
      <button
        type="button"
        data-action="toggle-sidebar"
        aria-label={railHidden ? 'Show Topics' : 'Hide Topics'}
        aria-expanded={railHidden ? 'false' : 'true'}
        aria-controls={`${WORKBENCH_ID}-left-rail`}
      >
        {icon(railHidden ? 'rail-show' : 'rail-hide')}
      </button>
    </ToolbarControlGroup>
  );
}

/**
 * The banners above the feed (NM-D5TYNV: kerf UI `StateBanner`s).
 *
 * They appear in response to background events (a failed check, a failing
 * topic), so they have to announce rather than wait to be found — which is why
 * the container is always present: a live region has to exist *before* its
 * content for the announcement to happen at all (NEWS-99). Each banner's root
 * names its kind in `data-banner`, which tests and the dismiss handlers key on,
 * and carries a stable `data-key`: these are conditional siblings, so without
 * one, removing an earlier banner would morph a later one into its place and
 * take a focused action with it (NM-QFJA60, Kerf beta.119 skill).
 */
function bannersJsx(
  s: AppState,
  ctx: {
    solo: Set<string>;
    reviewMode: boolean;
    savedCount: number;
    topicNames: Map<string, string>;
    lastFailure: ReturnType<typeof currentFailure>;
    pushNudge: ReturnType<typeof pushNudgeVariant>;
  },
): SafeHtml {
  const { solo, reviewMode, savedCount, topicNames, lastFailure, pushNudge } = ctx;
  const dismiss = (action: string, label = 'Dismiss', attrs: Record<string, string> = {}): SafeHtml => (
    <ToolbarControlGroup single appearance="borderless" size="compact">
      <button type="button" data-action={action} aria-label={label} {...attrs}>
        {icon('clear')}
      </button>
    </ToolbarControlGroup>
  );
  return (
    <div id="banners" role="status" aria-live="polite">
      {/* First, and above every other banner: it is the only account the user
          gets of why their topics are missing (NEWS-340). */}
      {s.quarantine !== null ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'quarantine', 'data-key': 'banner-quarantine' }}
          tone="danger"
          urgency="alert"
          copyLayout="stacked"
          icon={icon('warn')}
          title="The Database Could Not Be Read"
          detail={`This app's database could not be read on ${new Date(s.quarantine.at).toLocaleDateString()}, so it started with an empty one. Nothing was deleted — a copy of the old database is saved at ${s.quarantine.backupPath}. To restore it, stop the app and replace the data folder with that copy, then start again.`}
          action={dismiss('dismiss-quarantine')}
        />
      ) : (
        ''
      )}
      {/* Maintenance broadcast (NM-75): an operator-posted standing notice, no
          dismiss — it persists until the operator turns maintenance off. */}
      {s.maintenance.enabled && s.maintenance.message.trim() !== '' ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'maintenance', 'data-key': 'banner-maintenance' }}
          tone="warning"
          icon={icon('warn')}
          title="Maintenance"
          detail={s.maintenance.message}
        />
      ) : (
        ''
      )}
      {s.savedFilter ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'saved', 'data-key': 'banner-saved' }}
          tone="info"
          icon={icon('bookmark')}
          title={`Showing ${String(savedCount)} Saved ${savedCount === 1 ? 'Story' : 'Stories'}`}
          action={
            <Button size="compact" action="clear-saved-filter">
              Show All
            </Button>
          }
        />
      ) : (
        ''
      )}
      {solo.size > 0 ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'solo', 'data-key': 'banner-solo' }}
          tone="info"
          icon={icon('solo')}
          title={`Showing ${String(solo.size)} of ${String(s.topics.length)} Topics`}
          action={
            <Button size="compact" action="clear-solo">
              Show All
            </Button>
          }
        />
      ) : (
        ''
      )}
      {s.error !== null ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'error', 'data-key': 'banner-error' }}
          tone="danger"
          urgency="alert"
          icon={icon('warn')}
          title={s.error}
          action={dismiss('dismiss-error')}
        />
      ) : (
        ''
      )}
      {lastFailure !== null && s.error === null && lastFailure.id !== s.dismissedRunId ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'warn', 'data-key': 'banner-warn' }}
          tone="warning"
          icon={icon('warn')}
          title={`Last Check for “${topicNames.get(lastFailure.topicId) ?? 'Deleted Topic'}” Failed`}
          detail={lastFailure.error ?? 'unknown error'}
          action={dismiss('dismiss-warn', 'Dismiss', { 'data-run-id': lastFailure.id })}
        />
      ) : (
        ''
      )}
      {/* The push re-engagement nudge (NM-272, docs/51), least urgent of all.
          `pushNudgeVariant` (pure, unit-tested) decides which sentence, if any. */}
      {pushNudge === 'ready' ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'nudge-ready', 'data-key': 'banner-nudge-ready' }}
          icon={icon('bell')}
          copyLayout="stacked"
          title="Get Notified About Fresh Stories"
          detail="Your topics are now checked several times a day. Get a heads-up when fresh stories land — even with the app closed."
          action={[
            <Button size="compact" tone="brand" action="nudge-enable-notifications">
              Turn On Notifications
            </Button>,
            dismiss('dismiss-push-nudge', 'Not Now'),
          ]}
        />
      ) : pushNudge === 'ios-install' ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'nudge-ios-install', 'data-key': 'banner-nudge-ios-install' }}
          icon={icon('bell')}
          copyLayout="stacked"
          title="Get Notified About Fresh Stories"
          detail="On iPhone and iPad, add Newsmonger to your Home Screen first (Share → Add to Home Screen), then turn on notifications in Settings."
          action={dismiss('dismiss-push-nudge', 'Not Now')}
        />
      ) : pushNudge === 'blocked' ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'nudge-blocked', 'data-key': 'banner-nudge-blocked' }}
          tone="warning"
          icon={icon('bell')}
          copyLayout="stacked"
          title="Notifications Are Blocked"
          detail="Your browser is blocking notifications for this site. Allow them in the browser's site settings, then turn them on in Settings."
          action={dismiss('dismiss-push-nudge', 'Not Now')}
        />
      ) : (
        ''
      )}
      {reviewMode ? (
        <StateBanner
          rootAttributes={{ 'data-banner': 'review', 'data-key': 'banner-review' }}
          tone="warning"
          icon={icon('flag')}
          title={`Reviewing ${String(s.feedTotal)} Flagged ${s.feedTotal === 1 ? 'Story' : 'Stories'}${
            s.reviewTopicIds.length === 1 ? ` for ${topicNames.get(s.reviewTopicIds[0] ?? '') ?? 'a Topic'}` : ''
          }`}
          action={
            <Button size="compact" action="exit-review">
              Exit Review
            </Button>
          }
        />
      ) : (
        ''
      )}
    </div>
  );
}

/**
 * The Watching rail's content (kerf UI, NM-YGX7HY). The Workbench owns the
 * panel, its toolbar (title, sort, hide toggle) and its scroll; this is the
 * list inside it.
 *
 * - Plan usage (NM-135/227) as quiet badges — custom and catalog meter
 *   separately, so they show as separate parts, warning near and at the cap.
 * - Optimistic pending placeholders (NM-168) above the list, in their own
 *   live container so "Adding <name>…" is announced.
 * - The topic rows: one keyed `each()` (`key: 'topics'`), section headings as
 *   `ListHeader`s, the Disabled group as a `ListHeader` disclosure (NM-201).
 * The add field and the discover entry point moved to the feed toolbar (an
 * "Add Topic…" dialog and an icon button, NM-ZJT0H7).
 */
function railJsx(s: AppState, selected: Set<string>, solo: Set<string>, checking: Set<string>): SafeHtml {
  const pending = visiblePendingTopics(s.pendingTopics, s.topics);
  const menuSpec = topicMenuSpecFor(s, selected, solo);
  return (
    <List gap="xs" rootAttributes={{ 'data-rail': '' }}>
      {s.plan !== null ? (
        <Row gap="2xs" wrap vAlign="middle" controlInsets="rl" rootAttributes={{ 'data-plan-usage': '' }}>
          {planUsageParts(s.plan).map((part) => (
            <Badge
              size="compact"
              appearance="outline"
              tone={part.atLimit ? 'danger' : part.nearLimit ? 'warning' : 'neutral'}
              title={`Topics on your ${s.plan?.label ?? ''} plan`}
              rootAttributes={{
                'data-usage-kind': part.kind,
                'data-usage-state': part.atLimit ? 'at-limit' : part.nearLimit ? 'near-limit' : 'ok',
              }}
            >
              {part.cap === null
                ? `${String(part.used)} ${part.kind}`
                : `${String(part.used)} of ${String(part.cap)} ${part.kind}`}
            </Badge>
          ))}
        </Row>
      ) : null}
      {/* Optimistic pending-topic placeholders (NM-168). Always present: it is a
          live region, and a container appearing could otherwise shift the keyed
          list below it. */}
      <div class="pending-topics" aria-live="polite">
        {pending.map((p) => pendingTopicRowJsx(p.name, p.key))}
      </div>
      {/* `.map()` with `data-key`, not `each()`: a memoized `each()` row is
          swapped wholesale when it changes, which would close a row's open
          action menu (a live `PopupMenu`) whenever a poll updates that topic.
          The morph reconciles keyed rows in place instead, so an open menu
          survives, and an idle poll still renders identical HTML — no churn
          (NM-214). */}
      <List gap="2xs" rootAttributes={{ 'data-topic-list': '' }}>
        {topicRows(
          s.topics,
          s.topicSort,
          s.newestItemAtByTopic,
          s.disabledGroupOpen,
          s.plan !== null &&
            hasReEnableRoom(
              s.plan,
              s.topics.filter((t) => t.paused),
            ),
        ).map((row) =>
          isHeading(row) ? (
            row.disabled !== undefined ? (
              // The collapsible Disabled group (NM-201): a real disclosure carrying
              // the parked count; its rows follow only when expanded (`topicRows`).
              <div data-key={row.key} data-topic-section="disabled">
                <ListHeader
                  label="Disabled"
                  size="xs"
                  toggle
                  action="toggle-disabled-group"
                  expanded={row.disabled.open}
                  count={row.disabled.count}
                  countLabel={`${String(row.disabled.count)} disabled ${row.disabled.count === 1 ? 'topic' : 'topics'}`}
                />
                {/* The upgrade prompt's discoverable entry (NM-201): only when the
                      plan has room to turn some back on. */}
                {row.disabled.room ? (
                  <ListItem
                    action="open-enable-capacity"
                    density="compact"
                    icon={icon('play')}
                    label="Enable Topics…"
                  />
                ) : null}
              </div>
            ) : (
              <ListHeader
                label={row.label}
                size="xs"
                headingLevel={3}
                rootAttributes={{ 'data-key': row.key, 'data-topic-section': row.label }}
              />
            )
          ) : (
            topicRowJsx(
              row,
              selected.has(row.id),
              solo.has(row.id),
              solo.size > 0 && !solo.has(row.id),
              selected.size === 1 && selected.has(row.id),
              s.todayByTopic[row.id] ?? 0,
              // `Object.hasOwn`, not `!== undefined` (NEWS-264).
              Object.hasOwn(s.newestItemAtByTopic, row.id),
              s.pulseSparklines[row.id],
              checking.has(row.id),
              menuSpec(row.id),
            )
          ),
        )}
      </List>
      {s.loaded && s.topics.length === 0 && pending.length === 0 ? (
        <ListInsetText>
          <Text tone="quiet" size="compact">
            Nothing is being watched yet. Add a topic from the feed's toolbar — Newsmonger checks it on your schedule
            and reports only what's new.
          </Text>
        </ListInsetText>
      ) : null}
    </List>
  );
}

/**
 * The rail's heading (NM-WXNQ4F): a kerf UI `ListHeader` reading "Watching",
 * the page's level-2 heading for the topic list. With more than one topic to
 * order, its trailing action is the sort (NEWS-63/140): a button named for the
 * current order that opens a context `PopupMenu` of the orders beneath itself
 * (`openTopicSortMenu`) — a `ListHeader` action is a plain button, so the menu
 * is rendered beside it rather than as its trigger. The menu's items keep the
 * `topic-sort` action the handler has always matched.
 */
function railHeaderJsx(sort: (typeof TOPIC_SORTS)[number], sortable: boolean): SafeHtml {
  if (!sortable) return <ListHeader label="Watching" size="m" headingLevel={2} />;
  return (
    <div data-rail-header="">
      <ListHeader
        label="Watching"
        size="m"
        headingLevel={2}
        action="open-topic-sort"
        actionLabel={`Sort Topics: ${TOPIC_SORT_LABELS[sort]}`}
        actionIcon={icon('sort')}
        triggerAttributes={{ 'aria-haspopup': 'menu' }}
      />
      <PopupMenu
        context
        label="Sort Topics"
        rootAttributes={{ 'data-topic-sort-menu': '' }}
        items={TOPIC_SORTS.map((opt) => ({
          label: TOPIC_SORT_LABELS[opt],
          action: 'topic-sort',
          checked: opt === sort,
          attributes: { 'data-sort': opt },
        }))}
      />
    </div>
  );
}

/**
 * What each row's action menu acts on and says. Right-clicking (or opening
 * the "…" of) a selected row acts on the whole selection, the way every OS
 * file manager does; any other row acts on itself alone.
 */
function topicMenuSpecFor(s: AppState, selected: Set<string>, solo: Set<string>): (id: string) => TopicMenuSpec {
  const byId = new Map(s.topics.map((t) => [t.id, t]));
  return (id) => {
    const ids = selected.has(id) && selected.size > 1 ? [...selected] : [id];
    const targets = ids.map((t) => byId.get(t)).filter((t): t is NonNullable<typeof t> => t !== undefined);
    return {
      suffix: targets.length > 1 ? ` ${String(targets.length)} Topics` : '',
      anyActive: targets.some((t) => !t.paused),
      allSoloed: targets.length > 0 && targets.every((t) => solo.has(t.id)),
      flaggedCount: ids.reduce((sum, t) => sum + (s.flaggedByTopic[t] ?? 0), 0),
      single: targets.length === 1,
    };
  };
}

/** The work area's scrolling content: the feed, its empty state, paging, and the legal footer. */
function feedPaneJsx(
  s: AppState,
  feedItems: NewsItem[],
  topicNames: Map<string, string>,
  feedVariant: 'normal' | 'review',
  solo: Set<string>,
  reviewMode: boolean,
  searching: boolean,
  moreCount: number,
): SafeHtml {
  return (
    <div id="feed" class="feed">
      <div class="compact-pulse-slot">
        {solo.size === 1 && s.topicPulse !== null ? compactTopicPulseJsx(s.topicPulse) : ''}
      </div>
      {feedJsx(
        feedItems,
        topicNames,
        feedVariant,
        s.expandedItemId,
        {
          summaries: s.threads,
          panes: s.threadPanes,
          showAll: s.threadShowAll,
        },
        schemeDark.value ? 'dark' : 'light',
      )}
      <div class="empty-slot">
        {/* `feedLoaded`, not just `loaded` (NM-249): wait until the items request
            has answered before deciding the feed is empty. */}
        {s.loaded && s.feedLoaded && feedItems.length === 0 ? (
          <div data-empty-feed="">{feedEmptyStateJsx(s, reviewMode, searching)}</div>
        ) : (
          ''
        )}
      </div>
      {/* Always-present slot so the button appearing can't shift a keyed list above it. */}
      <div class="show-more-slot">
        {moreCount > 0 ? (
          <Button action="show-more">
            {`Show ${String(Math.min(moreCount, FEED_PAGE))} More${moreCount > FEED_PAGE ? ` (${String(moreCount)} left)` : ''}`}
          </Button>
        ) : (
          ''
        )}
      </div>
      {/* The legal links (NM-211) and copyright (NM-239), at the foot of the feed.
          Opened in a new tab so a reader keeps their place. */}
      <footer>
        <Row gap="m" wrap controlInsets="trbl">
          <a href="/privacy" target="_blank" rel="noopener">
            Privacy
          </a>
          <a href="/terms" target="_blank" rel="noopener">
            Terms
          </a>
          <Text variant="span" tone="quiet" size="compact">
            © {String(new Date().getFullYear())} Small Tale, Inc. All rights reserved.
          </Text>
        </Row>
      </footer>
    </div>
  );
}

/**
 * The dialog slots. Always-present containers, so a dialog appearing never
 * restructures its siblings (docs/3-ui.md).
 */
function dialogSlotsJsx(s: AppState): SafeHtml {
  const renameTarget = s.topics.find((t) => t.id === s.renameTopicId);
  const senseTarget = s.topics.find((t) => t.id === s.sensePickTopicId && isAwaitingSense(t));
  return (
    <div class="dialog-slots">
      <div id="onboarding-slot">
        {s.onboarding !== null && s.onboarding !== 'auto' ? onboardingJsx(s.onboarding) : ''}
      </div>
      <div id="settings-slot">{s.settingsOpen ? settingsDialogJsx() : ''}</div>
      <div id="add-topic-slot">{s.addTopicOpen ? addTopicDialogJsx() : ''}</div>
      <div id="confirm-slot">{s.confirm !== null ? confirmDialogJsx(s.confirm) : ''}</div>
      <div id="rename-slot">
        {renameTarget === undefined
          ? ''
          : renameDialogJsx(renameTarget, s.renameItemCount, s.renameCategory, s.renameSubcategory)}
      </div>
      <div id="sense-slot">
        {senseTarget === undefined
          ? ''
          : sensePickerDialogJsx(
              senseTarget,
              // Senses this account already follows a fork of (NM-164).
              s.topics
                .filter((t) => t.baseKey === senseTarget.baseKey && t.chosenSense !== null)
                .map((t) => t.chosenSense as string),
            )}
      </div>
      <div id="suggest-slot">{s.topicSuggest !== null ? suggestDialogJsx(s.topicSuggest) : ''}</div>
      <div id="capacity-slot">
        {s.capacityDialog !== null && s.plan !== null ? capacityDialogJsx(s.capacityDialog, s.topics, s.plan) : ''}
      </div>
      <div id="checkout-slot">
        {s.checkout !== null && s.plan !== null ? checkoutDialogJsx(s.checkout, s.plan.id, s.billing) : ''}
      </div>
      <div id="pulse-slot">{s.pulseDetailOpen ? pulseDialogJsx(s.pulseDetail, s.pulseLoading, s.pulseDays) : ''}</div>
      <div id="export-slot">
        {s.export !== null
          ? exportDialogJsx(s.export, sortTopics(s.topics, s.topicSort, s.newestItemAtByTopic), s.exporting)
          : ''}
      </div>
      <div id="discover-slot">{s.discover !== null ? discoverDialogJsx(s.discover) : ''}</div>
    </div>
  );
}

/**
 * The transient confirmation toast (NM-D5TYNV; kerf UI's toast look, NM-RR1Q7G).
 *
 * Store-driven rather than created with `toast()` from `kerfjs/overlay`: the
 * store holds the message and the timer in `raiseToast` clears it. The undo id
 * rides on the button's attribute, not the store, so a toast replaced
 * mid-click cannot undo the wrong topic (see docs/3-ui.md). `#toast-slot` stays
 * a polite live region that exists before its content (NEWS-99); `toast()`
 * would mount its own body-level region outside the render. The region and the
 * toast wear the `kerf-toasts` / `kerf-toast` classes `@kerfjs/ui/overlay-toast.css`
 * allows on app-owned elements, so kerf UI owns placement, surface and motion.
 * A plain toast is marked so it never takes a press (FR-26.8).
 */
function toastJsx(s: AppState): SafeHtml {
  const undoId = s.toast?.undoTopicId ?? null;
  return (
    <div id="toast-slot" aria-live="polite">
      <div class="kerf-toasts">
        {s.toast !== null ? (
          <div class="kerf-toast" role="status" data-toast="" data-toast-plain={undoId === null ? '' : undefined}>
            {undoId === null ? (
              s.toast.message
            ) : (
              <Row gap="xs" vAlign="middle" wrap>
                <Text variant="span">{s.toast.message}</Text>
                <Button size="compact" emphasis="plain" rootAttributes={{ 'data-undo-clear': undoId }}>
                  Undo
                </Button>
              </Row>
            )}
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
}
